import { provideHttpClient } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, isDevMode, provideAppInitializer } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { PermissionService } from 'app/core/auth/permission.service';
import { provideIcons } from 'app/core/icons/icons.provider';
import { MockApiService } from 'app/mock-api';
import { LookupService } from 'app/services/lookup.service';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { MarkdownModule } from 'ngx-markdown';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import keycloakOptions from './keycloack.config';

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(),
        provideRouter(appRoutes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // KeyCloack
        importProvidersFrom(KeycloakAngularModule, MarkdownModule.forRoot()),
        provideAppInitializer(async () => {
            const keycloak = inject(KeycloakService);
            const permissionService = inject(PermissionService);
            const lookupService = inject(LookupService);
            try {
                await keycloak.init({
                    config: keycloakOptions.config,
                    initOptions: keycloakOptions.initOptions,
                });
                const refreshToken = keycloak.getKeycloakInstance().refreshToken;
                if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

                const token_1 = keycloak.getKeycloakInstance().token;
                if (token_1) localStorage.setItem('token', token_1);

                // Load permissions from token so they're available on all routes (including error pages)
                permissionService.loadFromToken();

                // Load lookup data after auth is complete
                await lookupService.initialize();
            } catch (err) {
                console.error('Keycloak init failed', err);
            }
        }),
        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'it',
                        label: 'Italiano',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const defaultLang = translocoService.getDefaultLang();
            translocoService.setActiveLang(defaultLang);

            return firstValueFrom(translocoService.load(defaultLang));
        }),
        /*
        provideAiChat({
            api: {
                apiUrl: 'https://api.evolus.ai/',
                apiKey: '4E2AE8AB-32D7-4822-9781-F37EB6638F34',
                applicationId: '84101f24-419b-45f6-86d4-636438202310',
                agentId: '0f468722-d2ec-4d54-98e5-8f98383faf8e',
                projectId: 'd746afd0-ff1a-4750-5dda-08de7b8af60d',
            },
            openai: {
                apiKey: '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',
                tts: {
                    voice: 'echo',
                    model: 'gpt-4o-audio-preview',
                    speed: 1.0,
                },
                stt: {
                    model: 'whisper-1',
                    language: 'it',
                },
            },
            ui: {
                assistantName: 'Ritzy',
                position: { bottom: '20px', right: '20px' },
                panelSide: 'left',
                avatarSize: { floatingButton: '50px' },
                buttonGradient: {
                    from: 'transparent',
                    to: 'transparent',
                },

                customAvatarUrl: 'images/globbie.svg',
                useCustomAvatarInMessages: false,
                userAvatar: {
                    imageUrl: null as string | null,
                    initials: 'U',
                    displayName: 'User',
                },

                loadingMessages: [
                    'ai_chat.LOADING_MESSAGE_1',
                    'ai_chat.LOADING_MESSAGE_2',
                    'ai_chat.LOADING_MESSAGE_3',
                    'ai_chat.LOADING_MESSAGE_4',
                    'ai_chat.LOADING_MESSAGE_5',
                    'ai_chat.LOADING_MESSAGE_6',
                    'ai_chat.LOADING_MESSAGE_7',
                    'ai_chat.LOADING_MESSAGE_8',
                    'ai_chat.LOADING_MESSAGE_9',
                    'ai_chat.LOADING_MESSAGE_10',
                    'ai_chat.LOADING_MESSAGE_11',
                    'ai_chat.LOADING_MESSAGE_12',
                    'ai_chat.LOADING_MESSAGE_13',
                    'ai_chat.LOADING_MESSAGE_14',
                    'ai_chat.LOADING_MESSAGE_15',
                ],

                welcomeMessages: [
                    'ai_chat.WELCOME_MESSAGE_1',
                    'ai_chat.WELCOME_MESSAGE_2',
                    'ai_chat.WELCOME_MESSAGE_3',
                    'ai_chat.WELCOME_MESSAGE_4',
                    'ai_chat.WELCOME_MESSAGE_5',
                    'ai_chat.WELCOME_MESSAGE_6',
                    'ai_chat.WELCOME_MESSAGE_7',
                    'ai_chat.WELCOME_MESSAGE_8',
                    'ai_chat.WELCOME_MESSAGE_9',
                    'ai_chat.WELCOME_MESSAGE_10',
                ],

                borderGradientColors: [
                    '#bc82f3', // Purple
                    '#f5b9ea', // Pink
                    '#8d9fff', // Blue
                    '#aa6eee', // Purple
                    '#ff6778', // Red/Pink
                    '#ffba71', // Orange
                    '#c686ff', // Purple
                ],
                welcomeMessageDelay: 2000,
                borderAnimationDuration: 4,
                dimensions: {
                    width: '700px',
                    maxWidth: 'calc(100vw - 40px)',
                    height: '900px',
                    maxHeight: 'calc(100vh - 40px)',
                },
                icons: {
                    floatingButton: 'heroicons_outline:chat-bubble-left-right',
                    headerAvatar: 'heroicons_outline:sparkles',
                    aiAvatar: 'heroicons_outline:sparkles',
                    userAvatar: 'heroicons_outline:user',
                    sendButton: 'heroicons_outline:paper-airplane',
                    newChat: 'heroicons_outline:plus',
                    close: 'heroicons_outline:x-mark',
                    microphone: 'heroicons_outline:microphone',
                    microphoneRecording: 'heroicons_solid:microphone',
                    speaker: 'heroicons_outline:speaker-wave',
                    speakerPlaying: 'heroicons_solid:speaker-wave',
                },
            },
            text: {
                useTranslations: true,
                translationKeys: {
                    title: 'ai_chat.TITLE',
                    online: 'ai_chat.ONLINE',
                    typing: 'ai_chat.TYPING',
                    welcomeMessage: 'ai_chat.WELCOME_MESSAGE',
                    placeholder: 'ai_chat.INPUT_PLACEHOLDER',
                    sendButton: 'ai_chat.SEND',
                    newChat: 'ai_chat.NEW_CHAT',
                    close: 'ai_chat.CLOSE',
                    errorMessage: 'ai_chat.ERROR_MESSAGE',
                    errorNetwork: 'ai_chat.ERROR_NETWORK',
                    errorAuth: 'ai_chat.ERROR_AUTH',
                    errorServer: 'ai_chat.ERROR_SERVER',
                    errorValidation: 'ai_chat.ERROR_VALIDATION',
                    errorTimeout: 'ai_chat.ERROR_TIMEOUT',
                    errorRateLimit: 'ai_chat.ERROR_RATE_LIMIT',
                    errorUnknown: 'ai_chat.ERROR_UNKNOWN',
                    you: 'ai_chat.YOU',
                    settings: 'ai_chat.SETTINGS',
                    autoExecuteActions: 'ai_chat.AUTO_EXECUTE_ACTIONS',
                    autoCloseOnAction: 'ai_chat.AUTO_CLOSE_ON_ACTION',
                    ttsAutoRead: 'ai_chat.TTS_AUTO_READ',
                    autoExecuteActive: 'ai_chat.AUTO_EXECUTE_ACTIVE',
                    voiceInitializing: 'ai_chat.VOICE_INITIALIZING',
                    voiceRecording: 'ai_chat.VOICE_RECORDING',
                    voiceProcessing: 'ai_chat.VOICE_PROCESSING',
                    micInitializing: 'ai_chat.MIC_INITIALIZING',
                    startVoiceInput: 'ai_chat.START_VOICE_INPUT',
                    stopVoiceInput: 'ai_chat.STOP_VOICE_INPUT',
                    ttsLoading: 'ai_chat.TTS_LOADING',
                    ttsPlayMessage: 'ai_chat.TTS_PLAY_MESSAGE',
                    ttsStopMessage: 'ai_chat.TTS_STOP_MESSAGE',
                    componentActionsAvailable: 'ai_chat.COMPONENT_ACTIONS_AVAILABLE',
                    componentActionsIntro: 'ai_chat.COMPONENT_ACTIONS_INTRO',
                    actionNotAvailable: 'ai_chat.ACTION_NOT_AVAILABLE',
                    removeFile: 'ai_chat.REMOVE_FILE',
                    uploadDocument: 'ai_chat.UPLOAD_DOCUMENT',
                },
                textValues: {
                    title: 'AI Assistant',
                    online: 'Online',
                    typing: 'Typing...',
                    welcomeMessage: 'Hello! How can I help you today?',
                    placeholder: 'Type your message...',
                    sendButton: 'Send',
                    newChat: 'New chat',
                    close: 'Close',
                    errorMessage: 'An error occurred. Please try again.',
                    you: 'You',
                    loadingMessages: (assistantName: string): string[] => [
                        `${assistantName} is traveling around the world... 🌍`,
                        `${assistantName} is crossing the seas... 🌊`,
                        `${assistantName} is consulting its intergalactic archive... 🚀`,
                        `${assistantName} is touring Interglobo offices... 🏢`,
                        `${assistantName} is gathering information from 5 continents... 🗺️`,
                        `${assistantName} is browsing through databases... 📚`,
                        `${assistantName} is flying through data clouds... ☁️`,
                        `${assistantName} is coordinating with offices worldwide... 🌐`,
                        `${assistantName} is preparing the perfect answer... ✨`,
                        `${assistantName} is searching in its magic backpack... 🎒`,
                        `${assistantName} is crossing time zones... ⏰`,
                        `${assistantName} is exploring new horizons... 🧭`,
                        `${assistantName} is synchronizing continents... 🌏`,
                        `${assistantName} is navigating through data... ⛵`,
                        `${assistantName} is doing the global check-in... ✈️`,
                    ],
                    welcomeMessages: (assistantName: string): string[] => [
                        `Hello! I'm ${assistantName}, your global assistant. Ready to explore the world together?`,
                        `Welcome! I'm ${assistantName} and I just traveled around the world for you. How can I help?`,
                        `Hi! ${assistantName} at your service! I have information from all continents, feel free to ask!`,
                        `Greetings! I'm ${assistantName}, fresh back from an intercontinental trip. What are you looking for?`,
                        `Hey! ${assistantName} here! I just checked offices worldwide. How can I be of service?`,
                        `Hello! I'm ${assistantName}, your companion in the world of information!`,
                        `Welcome! ${assistantName} present! I have maps, data and answers from every corner of the globe!`,
                        `Hi! ${assistantName} reporting! Just landed from a tour of 5 continents. Tell me everything!`,
                        `Greetings! I'm ${assistantName} and my backpack is full of answers. What would you like to know?`,
                        `Hey! ${assistantName} here, ready to navigate through data with you! Where shall we start?`,
                    ],
                },
            },
            error: {
                useSharedCommands: true,
                customErrorHandler: null as ((message: string) => void) | null,
            },
            voice: {
                maxRecordingDuration: 60000, // 60 seconds
                waveColors: {
                    primary: '#667eea',
                    secondary: '#764ba2',
                },

                waveBars: 20,
            },
            storage: {
                conversationIdKey: 'evolus_conversation_id',
                autoExecuteActionsKey: 'evolus_auto_execute_actions',
                autoCloseOnActionKey: 'evolus_auto_close_on_action',
                autoTtsEnabledKey: 'evolus_auto_tts_enabled',
            },
            permissions: { useValue: { canUseFeature: () => true } },
        }),*/

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                service: MockApiService,
            },
            fuse: {
                layout: 'classic',
                scheme: 'dark',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-brand',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};
